// ── AgeGate.jsx ───────────────────────────────────────────────────────────────
// Professional age verification gate — shown once per device.
// • Reads saved language from localStorage to show correct language immediately.
// • Once user confirms 18+, flag is saved to localStorage permanently.
// • NO glitch / NO flash when switching tabs or returning to the tab.
// • Uses a module-level variable + immediate localStorage check to prevent
//   ANY rendering of the overlay if already verified — zero flash guaranteed.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import "./AgeGate.css";

// ── LocalStorage key ───────────────────────────────────────────────────────────
const AGE_KEY = "leaksmercy_age_verified";
const LANG_KEY = "app_language";

// ── Module-level verified flag ─────────────────────────────────────────────────
// This is evaluated ONCE when the module loads (before any React render).
// It will NEVER flip back to false during the lifetime of the page — this is
// what prevents the glitch when returning to the tab, because React may
// re-render but this module-level value stays "true" permanently.
let _moduleVerified = (() => {
  try {
    return localStorage.getItem(AGE_KEY) === "true";
  } catch {
    return false;
  }
})();

// ── Helper to detect saved language ───────────────────────────────────────────
function getSavedLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "ar" || saved === "en") return saved;
  } catch {}
  return "ar"; // default to Arabic
}

// ── Translations (inline — no dependency on LanguageContext) ───────────────────
const T = {
  ar: {
    brand:        "LeaksMercy",
    ageLabel:     "+18",
    warning:      "تحذير — محتوى للبالغين فقط",
    title:        "هذا الموقع يحتوي على محتوى مخصص للبالغين",
    subtitle:     "للدخول يجب أن يكون عمرك 18 عاماً أو أكثر. بالنقر على \"نعم، عمري +18\" فأنت تؤكد أنك بالغ وتتحمل المسؤولية الكاملة.",
    confirmBtn:   "نعم، عمري +18 — أدخل الموقع",
    denyBtn:      "لا، عمري أقل من 18 — مغادرة",
    legalNote:    "هذا الموقع مخصص حصراً للبالغين الذين يتجاوز عمرهم 18 عاماً. إذا كنت قاصراً أو كان القانون المحلي يحظر عليك مشاهدة هذا المحتوى، يُرجى المغادرة فوراً. باستخدامك لهذا الموقع فأنت تقر بأنك بالغ وتتحمل المسؤولية القانونية الكاملة.",
    deniedTitle:  "تم رفض الوصول",
    deniedText:   "عذراً، هذا الموقع مخصص للبالغين فقط (+18).\nيُرجى مغادرة هذه الصفحة.",
    deniedNote:   "إذا كنت تعتقد أن ذلك خطأ، يمكنك إعادة تحميل الصفحة.",
    reload:       "إعادة المحاولة",
    langHint:     "English",
    legalTitle:   "إقرار قانوني",
    adultContent: "محتوى للبالغين",
    secureNote:   "دخولك يعني قبولك للشروط والأحكام",
  },
  en: {
    brand:        "LeaksMercy",
    ageLabel:     "18+",
    warning:      "Warning — Adult Content Only",
    title:        "This website contains content intended for adults only",
    subtitle:     "To enter you must be 18 years of age or older. By clicking \"Yes, I am 18+\" you confirm you are an adult and take full responsibility.",
    confirmBtn:   "Yes, I am 18+ — Enter the Site",
    denyBtn:      "No, I am under 18 — Leave",
    legalNote:    "This site is intended exclusively for adults over 18 years of age. If you are a minor or local laws prohibit you from viewing this content, please leave immediately. By using this site you acknowledge that you are an adult and bear full legal responsibility.",
    deniedTitle:  "Access Denied",
    deniedText:   "Sorry, this website is for adults only (+18).\nPlease leave this page.",
    deniedNote:   "If you believe this is an error, you can reload the page.",
    reload:       "Try Again",
    langHint:     "عربي",
    legalTitle:   "Legal Disclaimer",
    adultContent: "Adult Content",
    secureNote:   "Entering means you accept the Terms & Conditions",
  },
};

// ── Shield / Lock icon SVG ─────────────────────────────────────────────────────
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="ag-shield-icon">
    <path
      d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
    />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" fill="none"
    />
  </svg>
);

// ── Warning icon ───────────────────────────────────────────────────────────────
const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="ag-warn-icon">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
    />
    <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const AgeGate = ({ children }) => {
  // ── EARLY EXIT: if already verified at module load, render children IMMEDIATELY
  // with zero overhead — no state, no effects, no overlay in DOM at all.
  if (_moduleVerified) {
    return <>{children}</>;
  }

  return <AgeGateInner>{children}</AgeGateInner>;
};

// ── Inner component (only mounted when NOT yet verified) ───────────────────────
// By splitting into two components we guarantee that once _moduleVerified is true
// the inner component is never mounted — so its animations never run again.
const AgeGateInner = ({ children }) => {
  const [lang, setLang] = useState(() => getSavedLang());

  // "pending" | "verified" | "denied"
  const [status, setStatus] = useState("pending");

  // Used only for the exit animation before switching to "verified"
  const [isExiting, setIsExiting] = useState(false);

  // Ref to track if component is still mounted (prevents setState after unmount)
  const mountedRef = useRef(true);

  const t = T[lang];

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Prevent scroll on body when gate is visible
  useEffect(() => {
    if (status !== "verified") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [status]);

  // ── visibilitychange guard ─────────────────────────────────────────────────
  // When the user switches tabs and comes back, the browser may re-trigger
  // paint/layout. We listen to visibilitychange: if the user comes back and
  // the localStorage flag is already set (e.g. they confirmed in another tab),
  // we immediately set status to verified so the overlay never flashes.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        try {
          if (localStorage.getItem(AGE_KEY) === "true") {
            // Update the module-level flag too
            _moduleVerified = true;
            if (mountedRef.current) {
              setStatus("verified");
            }
          }
        } catch {}
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const toggleLang = () => {
    const next = lang === "ar" ? "en" : "ar";
    setLang(next);
  };

  const handleConfirm = () => {
    // Persist to localStorage FIRST — before any state changes
    try {
      localStorage.setItem(AGE_KEY, "true");
      // Also update module-level flag immediately
      _moduleVerified = true;
    } catch {}

    setIsExiting(true);
    // Wait for fade-out animation then switch to verified
    setTimeout(() => {
      if (mountedRef.current) {
        setStatus("verified");
      }
    }, 550);
  };

  const handleDeny = () => {
    setStatus("denied");
  };

  const handleReload = () => {
    try {
      localStorage.removeItem(AGE_KEY);
      _moduleVerified = false;
    } catch {}
    setStatus("pending");
    setIsExiting(false);
  };

  // ── Verified → render children with no overlay ─────────────────────────────
  if (status === "verified") {
    return <>{children}</>;
  }

  // ── Denied Screen ──────────────────────────────────────────────────────────
  if (status === "denied") {
    return (
      <div
        className="ag-overlay ag-denied"
        dir={lang === "ar" ? "rtl" : "ltr"}
        lang={lang}
        role="alert"
        aria-live="assertive"
      >
        <div className="ag-bg" aria-hidden="true">
          <div className="ag-glow-1" />
          <div className="ag-glow-2" />
          <div className="ag-grid" />
        </div>

        <div className="ag-denied-card">
          <div className="ag-denied-icon" aria-hidden="true">
            <span>🚫</span>
          </div>
          <h2 className="ag-denied-title">{t.deniedTitle}</h2>
          <p className="ag-denied-text" style={{ whiteSpace: "pre-line" }}>{t.deniedText}</p>
          <p className="ag-denied-note">{t.deniedNote}</p>
          <button className="ag-reload-btn" onClick={handleReload}>
            {t.reload}
          </button>
        </div>
      </div>
    );
  }

  // ── Age Gate Screen (pending) ──────────────────────────────────────────────
  return (
    <div
      className={`ag-overlay${isExiting ? " ag-exiting" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
      lang={lang}
      role="dialog"
      aria-modal="true"
      aria-label={t.warning}
    >
      {/* Background */}
      <div className="ag-bg" aria-hidden="true">
        <div className="ag-glow-1" />
        <div className="ag-glow-2" />
        <div className="ag-grid" />
      </div>

      {/* Language toggle — top corner */}
      <button
        className="ag-lang-toggle"
        onClick={toggleLang}
        aria-label="Switch language"
        title="Switch language"
      >
        {t.langHint}
      </button>

      {/* Main card */}
      <div className="ag-card">
        {/* Brand header */}
        <div className="ag-brand">
          <div className="ag-brand-dot" aria-hidden="true" />
          <span className="ag-brand-name">
            Leaks<span>Mercy</span>
          </span>
          <div className="ag-brand-dot" aria-hidden="true" />
        </div>

        {/* Age badge */}
        <div className="ag-age-badge" aria-label={`Adults ${t.ageLabel} only`}>
          <span className="ag-age-number">{t.ageLabel}</span>
        </div>

        {/* Warning chip */}
        <div className="ag-warning-chip" role="status">
          <WarningIcon />
          <span>{t.warning}</span>
        </div>

        {/* Divider */}
        <div className="ag-divider" aria-hidden="true" />

        {/* Main title */}
        <h1 className="ag-title">{t.title}</h1>

        {/* Subtitle */}
        <p className="ag-subtitle">{t.subtitle}</p>

        {/* Action buttons */}
        <div className="ag-buttons">
          <button
            className="ag-btn ag-btn-confirm"
            onClick={handleConfirm}
            aria-label={t.confirmBtn}
          >
            <ShieldIcon />
            <span>{t.confirmBtn}</span>
          </button>

          <button
            className="ag-btn ag-btn-deny"
            onClick={handleDeny}
            aria-label={t.denyBtn}
          >
            <span>{t.denyBtn}</span>
          </button>
        </div>

        {/* Legal note */}
        <div className="ag-legal">
          <div className="ag-legal-header">
            <svg viewBox="0 0 16 16" aria-hidden="true" className="ag-legal-icon">
              <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <line x1="5" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <line x1="5" y1="9" x2="9" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <span>{t.legalTitle}</span>
          </div>
          <p className="ag-legal-text">{t.legalNote}</p>
        </div>

        {/* Footer note */}
        <p className="ag-footer-note">
          <svg viewBox="0 0 16 16" aria-hidden="true" className="ag-footer-icon">
            <path d="M8 1a5 5 0 100 10A5 5 0 008 1z" stroke="currentColor" strokeWidth="1.4" fill="none"/>
            <path d="M8 7v4M8 5.5v.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          {t.secureNote}
        </p>
      </div>
    </div>
  );
};

export default AgeGate;
